import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { StandardItem } from '../../types/standard';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { resolveColor } from '../../utils/colorUtils';
import { getTranslatedText, getTranslatedCategory } from '../../utils/translationHelpers';
import { WalkIcon, BikeIcon, GiftIcon } from '../icons';
import { getDisplayTravelType } from '../../utils/timeUtils';
import { formatTravelTime } from '../../utils/formatDuration';

interface SectionColorInfo {
  sectionId: string;
  sectionName: string;
  sectionColor: string;
}

interface ExploreListViewProps {
  items: (StandardItem & SectionColorInfo)[];
  onItemClick: (item: StandardItem) => void;
  onSectionFilter: (sectionId: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
}

export const ExploreListView: React.FC<ExploreListViewProps> = ({
  items,
  onItemClick,
  onSectionFilter,
  config,
  translations,
}) => {
  const { i18n } = useTranslation();
  const backgroundColor = resolveColor(config['sectionBack-color'], config, '#f9fafb');

  const ListItemCard = React.memo(({ event }: { event: StandardItem & SectionColorInfo }) => {
    const imageUrl = (event as any).image_metadata?.url || event.image;
    const { displayImage, shouldDisplay } = useImageDisplay(imageUrl, 'listCard');

    const renderTravelTime = () => {
      const limitWalk = parseInt(config.limit_walk || '25', 10);
      const travelType = getDisplayTravelType(event.timewalk, event.timebike, event.timedrive, limitWalk);

      switch (travelType) {
        case 'walk':
          return (
            <div className="flex items-center">
              <WalkIcon size={16} className="mr-1" />
              <span>{formatTravelTime(event.timewalk, i18n.language)}</span>
            </div>
          );
        case 'bike':
          return (
            <div className="flex items-center">
              <BikeIcon size={16} className="mr-1" />
              <span>{formatTravelTime(event.timebike, i18n.language)}</span>
            </div>
          );
        case 'drive':
          return (
            <div className="flex items-center">
              <Car size={16} className="mr-1" fill="currentColor" />
              <span>{formatTravelTime(event.timedrive, i18n.language)}</span>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div
        className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 relative"
        style={{ borderRadius: config.border_radius }}
      >
        {event.offer && (
          <div
            className="absolute top-0 right-0 w-10 h-10 flex items-center justify-center z-10"
            style={{
              backgroundColor: config['major-color'],
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius,
            }}
          >
            <GiftIcon size={20} className="text-white" />
          </div>
        )}

        <div className="cursor-pointer" onClick={() => onItemClick(event)}>
          {shouldDisplay && displayImage && (
            <div className="relative pb-[66.666%]">
              <img
                src={displayImage}
                alt={event.name}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-3 sm:p-4">
            <h3 className="text-base sm:text-lg font-medium mb-1 sm:mb-2">{event.name}</h3>

            {event.fulladress && (
              <p className="text-sm text-gray-600 mb-1">{event.fulladress}</p>
            )}

            <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
              {event.distance && (
                <div className="flex items-center text-gray-600">
                  <MapPin size={16} className="mr-1" />
                  <span>{event.distance}km</span>
                </div>
              )}
              {renderTravelTime()}
              {event.price && (
                <div className="flex items-center" style={{ color: config['major-color'] }}>
                  <Tag size={16} className="mr-1" />
                  <span>{event.price}</span>
                </div>
              )}
              {event.ouverture_lundi === 'oui' && (
                <div className="flex items-center text-gray-600">
                  <Clock size={16} className="mr-1" />
                  <span>{getTranslatedText(translations, 'events_monday_open', i18n.language)}</span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSectionFilter(event.sectionId);
                }}
                className="inline-block px-1.5 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-white"
                style={{ backgroundColor: event.sectionColor }}
              >
                {event.sectionName}
              </button>
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="inline-block px-1.5 py-0.5 text-xs rounded text-white"
                style={{ backgroundColor: config['dark-color'] }}
              >
                {getTranslatedCategory(translations, event.category, i18n.language)}
              </button>
              {event.type && (
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block px-1.5 py-0.5 text-xs rounded text-white opacity-70"
                  style={{ backgroundColor: config['medium-color'] }}
                >
                  {getTranslatedCategory(translations, event.type, i18n.language)}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (!items || items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor }}>
        <p className="text-gray-500">
          {getTranslatedText(translations, 'no_results', i18n.language) || 'Aucun résultat'}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pb-20" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {items.map((event) => (
            <ListItemCard key={`${(event as any)._sectionId}_${event.id}`} event={event} />
          ))}
        </div>
      </div>
    </div>
  );
};
